/**
 * IntegrationMapPage — Platform page wrapping the integration map view.
 *
 * Fetches data from the integration map API and renders inside ViewShell.
 *
 * WQ-099: Phase 4.6.1 Executive Views — Step 6 of 6
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAdminContext } from '../../contexts/AdminContextProvider';
import ViewShell from './ViewShell';
import IntegrationMapView from '../../components/integration-map/IntegrationMapView';
import { fetchFullMap } from '../../services/integration-map-api';
import type { IntegrationMap } from '../../services/integration-map-api';
import type { ViewContext } from '../StubPage';

interface Props {
  context?: ViewContext;
}

export default function IntegrationMapPage({ context }: Props) {
  const { authFetch } = useAuth();
  const { activeTenant } = useAdminContext();
  const [data, setData] = useState<IntegrationMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const map = await fetchFullMap(authFetch, activeTenant?.id);
      setData(map);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load integration map');
    } finally {
      setLoading(false);
    }
  }, [authFetch, activeTenant]);

  useEffect(() => { void load(); }, [load]);

  return (
    <ViewShell
      context={context}
      title="Functional Integration Map"
      icon="git-merge"
      subtitle="Cross-departmental operational relationships"
      actions={
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      }
    >
      {loading && !data ? (
        <div style={{ textAlign: 'center', padding: '64px' }}>
          <div className="spinner" style={{ marginBottom: '12px' }} />
          <p style={{ color: 'var(--text-muted)' }}>Loading integration map...</p>
        </div>
      ) : error && !data ? (
        <div style={{ textAlign: 'center', padding: '64px' }}>
          <p style={{ color: '#ef4444', marginBottom: '8px' }}>Failed to load integration map</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{error}</p>
          <button onClick={load} className="btn btn-primary" style={{ marginTop: '16px' }}>Retry</button>
        </div>
      ) : data ? (
        <IntegrationMapView data={data} onRefresh={load} />
      ) : null}
    </ViewShell>
  );
}
